import React, { useEffect, useState, useMemo, useCallback } from "react";
import { getAnalyst } from "../lib/api";
import { Bot, TrendingUp, TrendingDown, Minus, Target, AlertTriangle, Shield, Activity, Zap, ChevronDown, ChevronRight, Info } from "lucide-react";
import SectionExplanationModal from "./SectionExplanationModal";

// Color palette mapped to bullish / neutral / bearish semantics.
const TONE_COLORS = {
    bullish: "#22c55e",
    bearish: "#ef4444",
    neutral: "#eab308",
    long: "#22c55e",
    short: "#ef4444",
    wait: "#eab308",
};

const trendTone = (t) => {
    if (!t) return "neutral";
    if (t.includes("Bullish")) return "bullish";
    if (t.includes("Bearish")) return "bearish";
    return "neutral";
};

const fmtNum = (n) => (n == null ? "—" : Number(n).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 4 }));
const scoreColor = (n) => n >= 81 ? "#22c55e" : n >= 61 ? "#3b82f6" : n >= 41 ? "#eab308" : "#ef4444";

/**
 * AI Trading Analyst side panel.
 *
 * Reads the combined deterministic snapshot + LLM narration from /api/analyst,
 * renders a 4-section UI (executive, score gauge, levels, plan, deep dive).
 * Notifies the parent via `onLevels` whenever new S/R prices are produced
 * so they can be drawn directly on the trading chart.
 */
export default function AIAnalystPanel({ symbol, onLevels, currentPrice }) {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [openSections, setOpenSections] = useState({ indicators: false, patterns: true, scenarios: true, volume: true });
    // On-demand section explanation modal state
    const [modalSection, setModalSection] = useState(null); // { type, label, data } | null
    const openExplain = useCallback((section) => setModalSection(section), []);
    const closeExplain = useCallback(() => setModalSection(null), []);

    useEffect(() => {
        if (!symbol) return;
        let cancelled = false;
        setLoading(true);
        setError(null);
        getAnalyst(symbol)
            .then((d) => {
                if (cancelled) return;
                setData(d);
                // Push key levels back up so the chart can render them
                const lvls = d?.analysis?.key_levels;
                if (lvls && onLevels) {
                    onLevels({
                        supports: (lvls.supports || []).map(s => ({ price: s.price, label: s.label })),
                        resistances: (lvls.resistances || []).map(r => ({ price: r.price, label: r.label })),
                    });
                }
            })
            .catch((e) => { if (!cancelled) setError(e?.response?.data?.detail || e.message); })
            .finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
    }, [symbol, onLevels]);

    const a = data?.analysis;
    const mc = data?.market_context;
    const trendColor = TONE_COLORS[trendTone(a?.trend_classification)];

    return (
        <div data-testid="ai-analyst-panel" className="border border-soft bg-elev">
            <div className="flex items-center gap-2 px-4 py-3 border-b border-soft" style={{ background: "rgba(59,130,246,0.08)" }}>
                <Bot size={16} style={{ color: "var(--brand-blue)" }} />
                <div className="text-xs font-bold tracking-wider" style={{ color: "var(--brand-blue)" }}>AI TRADING ANALYST</div>
                <div className="text-[10px] text-mute ml-auto">Claude Sonnet 4.5</div>
            </div>

            {loading && <SkeletonState />}
            {error && (
                <div className="p-4 text-xs" style={{ color: "var(--red)" }}>
                    <AlertTriangle size={12} className="inline mr-1" />
                    Errore: {error}
                </div>
            )}
            {!loading && a && (
                <div className="divide-y divide-soft">
                    {/* Summary */}
                    <Section
                        title="Riassunto esecutivo"
                        sectionType="riassunto_esecutivo"
                        sectionData={{
                            trend_classification: a.trend_classification,
                            final_bias: a.final_bias,
                            mtf: a.multi_timeframe_alignment,
                            trend_breakdown: a.trend_breakdown,
                        }}
                        onExplain={openExplain}
                    >
                        {a.summary && (
                            <div className="text-xs leading-relaxed text-dim" data-testid="analyst-summary">{a.summary}</div>
                        )}
                        <div className={a.summary ? "mt-3 flex flex-wrap items-center gap-2" : "flex flex-wrap items-center gap-2"}>
                            <TrendBadge trend={a.trend_classification} color={trendColor} />
                            {a.final_bias && <BiasBadge bias={a.final_bias} />}
                            {a.multi_timeframe_alignment && (
                                <span data-testid="mtf-pill" className="text-[10px] px-2 py-1 border border-soft tracking-wider"
                                    style={{ color: a.multi_timeframe_alignment.aligned ? "var(--green)" : "var(--text-dim)" }}>
                                    MTF {a.multi_timeframe_alignment.aligned ? "ALLINEATA" : "DIVERGENTE"}
                                </span>
                            )}
                        </div>
                        {a.trend_breakdown && (
                            <dl className="mt-3 grid grid-cols-1 gap-1 text-[11px]">
                                <Row k="Primario" v={a.trend_breakdown.primary} />
                                <Row k="Secondario" v={a.trend_breakdown.secondary} />
                                <Row k="Breve termine" v={a.trend_breakdown.short_term} />
                            </dl>
                        )}
                    </Section>

                    {/* Score */}
                    <Section title="Trader Score"
                        sectionType="trader_score"
                        sectionData={{ scoring: a.scoring }}
                        onExplain={openExplain}
                    >
                        <ScoreGauge scoring={a.scoring} />
                    </Section>

                    {/* Long vs Short comparison */}
                    {a.comparison && (
                        <Section title="Long vs Short"
                            sectionType="long_vs_short"
                            sectionData={{ comparison: a.comparison }}
                            onExplain={openExplain}
                        >
                            <LongShortBar c={a.comparison} />
                        </Section>
                    )}

                    {/* Key levels */}
                    <Section title="Livelli chiave"
                        sectionType="livelli_chiave"
                        sectionData={{ key_levels: a.key_levels, current_price: currentPrice ?? a.trade_plan?.current_price }}
                        onExplain={openExplain}
                    >
                        <LevelsList levels={a.key_levels} currentPrice={currentPrice ?? a.trade_plan?.current_price} />
                    </Section>

                    {/* Long plan */}
                    <Section title="Piano LONG"
                        sectionType="piano_long"
                        sectionData={{ trade_plan: a.trade_plan }}
                        onExplain={openExplain}
                    >
                        <TradePlan plan={a.trade_plan} side="long" />
                    </Section>

                        <Section title="Piano SHORT"
                            sectionType="piano_short"
                            sectionData={{ short_plan: a.short_plan }}
                            onExplain={openExplain}
                        >
                            <ShortPlan plan={a.short_plan} onExplain={openExplain} />
                        </Section>

                    {/* Scenarios */}
                    <CollapsibleSection title="Scenari probabilistici" open={openSections.scenarios}
                        onToggle={() => setOpenSections(s => ({ ...s, scenarios: !s.scenarios }))}
                        sectionType="scenari_probabilistici"
                        sectionData={{ scenarios: a.scenarios }}
                        onExplain={openExplain}
                    >
                        <Scenarios s={a.scenarios} />
                    </CollapsibleSection>

                    {/* Volume analysis */}
                    {a.volume_analysis && (
                        <CollapsibleSection title="Analisi volumetrica" open={openSections.volume ?? true}
                            onToggle={() => setOpenSections(s => ({ ...s, volume: !(s.volume ?? true) }))}
                            sectionType="analisi_volumetrica"
                            sectionData={{ volume_analysis: a.volume_analysis }}
                            onExplain={openExplain}
                        >
                            <VolumeAnalysis v={a.volume_analysis} onExplain={openExplain} />
                        </CollapsibleSection>
                    )}

                    {/* Market Context (additive: Relative Strength + Market Regime + Smart SL/TP) */}
                    {mc && (
                        <CollapsibleSection title="Market Context" open={openSections.mkt_ctx ?? true}
                            onToggle={() => setOpenSections(s => ({ ...s, mkt_ctx: !(s.mkt_ctx ?? true) }))}
                            sectionType="market_context"
                            sectionData={{ market_context: mc }}
                            onExplain={openExplain}
                        >
                            <MarketContext mc={mc} />
                        </CollapsibleSection>
                    )}

                    {/* Liquidity Map + Trend Structure — institutional indicators
                        (FVG, Order Blocks, Stop Hunt, HH/HL structure). Pure
                        deterministic add-on; never blocks rendering if missing. */}
                    {(data?.liquidity_map || data?.trend_structure) && (
                        <CollapsibleSection title="Liquidity & Trend Structure" open={openSections.liquidity ?? true}
                            onToggle={() => setOpenSections(s => ({ ...s, liquidity: !(s.liquidity ?? true) }))}>
                            <LiquidityPanel liquidity={data.liquidity_map} trendStructure={data.trend_structure} />
                        </CollapsibleSection>
                    )}

                    {/* Patterns */}
                    {a.patterns && a.patterns.length > 0 && (
                        <CollapsibleSection title={`Pattern grafici (${a.patterns.length})`} open={openSections.patterns}
                            onToggle={() => setOpenSections(s => ({ ...s, patterns: !s.patterns }))}>
                            <ul className="space-y-2">
                                {a.patterns.map((p, i) => (
                                    <li key={i} className="text-[11px] border-l-2 pl-2"
                                        style={{ borderColor: TONE_COLORS[p.implication] || "var(--text-mute)" }}>
                                        <div className="font-semibold text-dim flex items-center gap-2">
                                            {p.name}
                                            <span className="text-[9px] px-1.5 py-0.5" style={{ background: "rgba(255,255,255,0.04)" }}>{p.status}</span>
                                        </div>
                                        <div className="text-mute mt-0.5">{p.detail}</div>
                                    </li>
                                ))}
                            </ul>
                        </CollapsibleSection>
                    )}

                    {/* Indicators */}
                    {a.indicators && a.indicators.length > 0 && (
                        <CollapsibleSection title={`Indicatori (${a.indicators.length})`} open={openSections.indicators}
                            onToggle={() => setOpenSections(s => ({ ...s, indicators: !s.indicators }))}>
                            <ul className="space-y-2">
                                {a.indicators.map((ind, i) => <IndicatorRow key={i} ind={ind} />)}
                            </ul>
                        </CollapsibleSection>
                    )}

                    {/* Risk warnings */}
                    {a.risk_warnings && a.risk_warnings.length > 0 && (
                        <div className="px-4 py-3 text-[10px]" style={{ background: "rgba(239,68,68,0.06)", color: "var(--text-dim)" }}>
                            <div className="flex items-center gap-1 mb-1.5" style={{ color: "var(--red)" }}>
                                <Shield size={11} />
                                <span className="font-semibold tracking-wider">AVVERTENZE</span>
                            </div>
                            <ul className="space-y-1 leading-relaxed">
                                {a.risk_warnings.map((w, i) => <li key={i}>• {w}</li>)}
                            </ul>
                        </div>
                    )}
                </div>
            )}

            {/* On-demand section explanation modal (Claude call fires only on open) */}
            <SectionExplanationModal
                open={!!modalSection}
                onClose={closeExplain}
                assetSymbol={symbol}
                sectionType={modalSection?.type}
                sectionLabel={modalSection?.label}
                sectionData={modalSection?.data}
            />
        </div>
    );
}

// ---------- sub-components ----------

/**
 * Section — clickable wrapper: click anywhere on the card fires
 * `onExplain({type, label, data})`. Purely additive: if no `sectionType`
 * or no `onExplain` is passed, behaves as the plain non-clickable version.
 */
const Section = ({ title, children, sectionType, sectionData, onExplain }) => {
    const clickable = !!(sectionType && onExplain);
    const handleClick = () => {
        if (!clickable) return;
        onExplain({ type: sectionType, label: title, data: sectionData || {} });
    };
    return (
        <div
            className={"px-4 py-3 " + (clickable ? "cursor-pointer transition-colors hover:bg-white/[0.02] group" : "")}
            onClick={clickable ? handleClick : undefined}
            data-testid={clickable ? `section-${sectionType}` : undefined}
        >
            <div className="flex items-center gap-2 mb-2">
                <div className="text-[10px] text-mute tracking-widest">{title.toUpperCase()}</div>
                {clickable && (
                    <Info
                        size={11}
                        className="text-mute opacity-60 group-hover:opacity-100 group-hover:text-brand-blue transition-opacity"
                        style={{ color: "var(--brand-blue)" }}
                        aria-label="Spiega questa sezione"
                    />
                )}
            </div>
            {children}
        </div>
    );
};

/**
 * CollapsibleSection — the existing expand/collapse toggle stays untouched;
 * an additional (independent) ⓘ button is exposed next to the title so the
 * user can request an explanation WITHOUT triggering the toggle. The button
 * uses stopPropagation so it never collides with the collapse click.
 */
const CollapsibleSection = ({ title, open, onToggle, children, sectionType, sectionData, onExplain }) => {
    const hasExplain = !!(sectionType && onExplain);
    return (
        <div className="px-4 py-3">
            <div className="flex items-center gap-2 mb-2">
                <button onClick={onToggle} className="flex items-center gap-1 text-[10px] text-mute tracking-widest hover:text-foreground text-left">
                    {open ? <ChevronDown size={11} /> : <ChevronRight size={11} />}
                    {title.toUpperCase()}
                </button>
                {hasExplain && (
                    <button
                        type="button"
                        data-testid={`explain-${sectionType}`}
                        onClick={(e) => {
                            e.stopPropagation();
                            onExplain({ type: sectionType, label: title, data: sectionData || {} });
                        }}
                        className="p-0.5 opacity-60 hover:opacity-100 transition-opacity"
                        aria-label="Spiega questa sezione"
                        title="Spiega questa sezione"
                    >
                        <Info size={11} style={{ color: "var(--brand-blue)" }} />
                    </button>
                )}
            </div>
            {open && <div className="pl-3">{children}</div>}
        </div>
    );
};

const Row = ({ k, v }) => (
    <div className="flex justify-between gap-3"><span className="text-mute">{k}</span><span className="text-dim text-right">{v}</span></div>
);

const TrendBadge = ({ trend, color }) => {
    const Icon = trend?.includes("Bullish") ? TrendingUp : trend?.includes("Bearish") ? TrendingDown : Minus;
    return (
        <span data-testid="trend-badge" className="inline-flex items-center gap-1 text-[10px] px-2 py-1 border tracking-wider font-bold"
            style={{ color, borderColor: color }}>
            <Icon size={11} />
            {trend}
        </span>
    );
};

function ScoreGauge({ scoring }) {
    if (!scoring) return null;
    const total = scoring.total ?? 0;
    const cls = scoring.classification || "—";
    const color = total >= 81 ? "#22c55e" : total >= 61 ? "#3b82f6" : total >= 41 ? "#eab308" : "#ef4444";
    const subs = [
        { k: "Trend", v: scoring.trend_strength },
        { k: "Momentum", v: scoring.momentum },
        { k: "Volume", v: scoring.volume_confirmation },
        { k: "Volatilità", v: scoring.volatility_quality },
        { k: "Setup", v: scoring.setup_quality },
    ];
    return (
        <div data-testid="score-gauge">
            <div className="flex items-end gap-3">
                <div className="text-4xl font-bold tnum" style={{ color }}>{total}</div>
                <div className="text-[10px] text-mute pb-1.5">/100</div>
                <div className="ml-auto text-[10px] tracking-wider font-semibold" style={{ color }}>{cls}</div>
            </div>
            <div className="mt-2 h-1.5 bg-elev2 relative overflow-hidden">
                <div className="absolute inset-y-0 left-0 transition-all" style={{ width: `${total}%`, background: color }} />
            </div>
            {(scoring.long_score != null || scoring.short_score != null) && (
                <div className="mt-3 grid grid-cols-2 gap-2">
                    <div className="border border-soft px-2 py-1.5" style={{ background: "rgba(34,197,94,0.06)" }}>
                        <div className="text-[9px] tracking-widest text-mute">LONG SCORE</div>
                        <div className="text-lg font-bold tnum" style={{ color: "var(--green)" }}>{scoring.long_score ?? 0}<span className="text-[9px] text-mute">/100</span></div>
                    </div>
                    <div className="border border-soft px-2 py-1.5" style={{ background: "rgba(239,68,68,0.06)" }}>
                        <div className="text-[9px] tracking-widest text-mute">SHORT SCORE</div>
                        <div className="text-lg font-bold tnum" style={{ color: "var(--red)" }}>{scoring.short_score ?? 0}<span className="text-[9px] text-mute">/100</span></div>
                    </div>
                </div>
            )}
            <ul className="mt-3 grid grid-cols-5 gap-1">
                {subs.map(s => (
                    <li key={s.k} className="text-center">
                        <div className="text-[9px] text-mute uppercase">{s.k}</div>
                        <div className="text-xs font-bold tnum" style={{ color: (s.v ?? 0) >= 14 ? "var(--green)" : (s.v ?? 0) >= 8 ? "var(--amber)" : "var(--red)" }}>{s.v ?? 0}</div>
                        <div className="text-[8px] text-mute">/20</div>
                    </li>
                ))}
            </ul>
        </div>
    );
}

function LevelsList({ levels, currentPrice }) {
    if (!levels) return null;
    const sups = levels.supports || [];
    const reses = levels.resistances || [];
    return (
        <div className="grid grid-cols-2 gap-3 text-[11px]" data-testid="levels-list">
            <div>
                <div className="text-[9px] tracking-widest text-mute mb-1" style={{ color: "var(--red)" }}>RESISTENZE</div>
                <ul className="space-y-1">
                    {reses.length === 0 && <li className="text-mute">—</li>}
                    {reses.map((r, i) => (
                        <li key={i} className="flex items-baseline justify-between gap-2">
                            <span className="tnum font-semibold" style={{ color: "var(--red)" }}>{fmtNum(r.price)}</span>
                            <span className="text-[9px] uppercase text-mute">{r.label || ""}</span>
                        </li>
                    ))}
                </ul>
            </div>
            <div>
                <div className="text-[9px] tracking-widest text-mute mb-1" style={{ color: "var(--green)" }}>SUPPORTI</div>
                <ul className="space-y-1">
                    {sups.length === 0 && <li className="text-mute">—</li>}
                    {sups.map((s, i) => (
                        <li key={i} className="flex items-baseline justify-between gap-2">
                            <span className="tnum font-semibold" style={{ color: "var(--green)" }}>{fmtNum(s.price)}</span>
                            <span className="text-[9px] uppercase text-mute">{s.label || ""}</span>
                        </li>
                    ))}
                </ul>
            </div>
            {currentPrice != null && (
                <div className="col-span-2 text-[10px] text-mute mt-1">Prezzo attuale <span className="tnum text-dim font-semibold ml-1">{fmtNum(currentPrice)}</span></div>
            )}
        </div>
    );
}

function TradePlan({ plan, side = "long" }) {
    if (!plan) return null;
    const dirColor = TONE_COLORS[plan.direction] || "var(--text-mute)";
    const sideColor = side === "short" ? "var(--red)" : "var(--green)";
    return (
        <div data-testid={`trade-plan-${side}`} className="space-y-2 text-[11px]">
            <div className="flex items-center gap-2">
                <Target size={11} style={{ color: sideColor }} />
                <span className="font-bold tracking-widest uppercase" style={{ color: dirColor }}>
                    Direzione: {plan.direction || "—"}
                </span>
                {plan.buy_zone && (
                    <span className="ml-auto text-mute text-[10px]">
                        Zona entry: <span className="tnum text-dim">{fmtNum(plan.buy_zone.low)} – {fmtNum(plan.buy_zone.high)}</span>
                    </span>
                )}
            </div>
            <div className="grid grid-cols-2 gap-x-3 gap-y-1.5">
                <PlanRow label="Entry conservativo" value={plan.entry_conservative} tone="brand" />
                <PlanRow label="Entry aggressivo" value={plan.entry_aggressive} tone="brand" />
                <PlanRow label="Stop Loss" value={plan.stop_loss} tone="bearish" />
                <PlanRow label="Target 1" value={plan.target_1} tone="bullish" extra={`R/R ${plan.risk_reward_t1 ?? "—"}`} />
                <PlanRow label="Target 2" value={plan.target_2} tone="bullish" extra={`R/R ${plan.risk_reward_t2 ?? "—"}`} />
                <PlanRow label="Target 3" value={plan.target_3} tone="bullish" extra={`R/R ${plan.risk_reward_t3 ?? "—"}`} />
            </div>
        </div>
    );
}

function ShortPlan({ plan, onExplain }) {
    if (!plan) return <div className="text-[11px] text-mute">—</div>;
    if (!plan.available) {
        return (
            <div data-testid="short-plan-unavailable" className="text-[11px] text-mute leading-relaxed">
                <span className="inline-block px-2 py-0.5 mr-2 text-[9px] tracking-wider border"
                    style={{ color: "var(--text-mute)", borderColor: "var(--border-soft)" }}>NON DISPONIBILE</span>
                {plan.rationale_if_unavailable || "Nessun setup short di qualità in questo momento."}
            </div>
        );
    }
    return (
        <div data-testid="short-plan" className="space-y-2 text-[11px]">
            <div className="flex items-center gap-2">
                <Target size={11} style={{ color: "var(--red)" }} />
                <span className="font-bold tracking-widest uppercase" style={{ color: "var(--red)" }}>SHORT SETUP</span>
                {plan.short_zone && (
                    <span className="ml-auto text-mute text-[10px]">
                        Zona entry: <span className="tnum text-dim">{fmtNum(plan.short_zone.low)} – {fmtNum(plan.short_zone.high)}</span>
                    </span>
                )}
            </div>
            <div className="grid grid-cols-2 gap-x-3 gap-y-1.5">
                <PlanRow label="Entry conservativo" value={plan.entry_conservative} tone="brand" />
                <PlanRow label="Entry aggressivo" value={plan.entry_aggressive} tone="brand" />
                <PlanRow label="Stop Loss" value={plan.stop_loss} tone="bullish" />
                <PlanRow label="Target 1" value={plan.target_1} tone="bearish" extra={`R/R ${plan.risk_reward_t1 ?? "—"}`} />
                <PlanRow label="Target 2" value={plan.target_2} tone="bearish" extra={`R/R ${plan.risk_reward_t2 ?? "—"}`} />
                <PlanRow label="Target 3" value={plan.target_3} tone="bearish" extra={`R/R ${plan.risk_reward_t3 ?? "—"}`} />
            </div>
            {plan.trigger_conditions && plan.trigger_conditions.length > 0 && (
                <ClickableWrap
                    onExplain={onExplain}
                    sectionType="condizioni_innesco"
                    sectionLabel="Condizioni di Innesco"
                    sectionData={{ trigger_conditions: plan.trigger_conditions }}
                >
                    <div className="mt-2 pt-2 border-t border-soft">
                        <div className="text-[9px] tracking-widest text-mute mb-1">CONDIZIONI DI INNESCO</div>
                        <ul className="space-y-0.5 text-[10px] text-dim leading-relaxed">
                            {plan.trigger_conditions.map((t, i) => <li key={i}>• {t}</li>)}
                        </ul>
                    </div>
                </ClickableWrap>
            )}
        </div>
    );
}

function BiasBadge({ bias }) {
    const isLong = bias.includes("Long");
    const isShort = bias.includes("Short");
    const color = isLong ? "var(--green)" : isShort ? "var(--red)" : "var(--amber)";
    return (
        <span data-testid="final-bias-badge" className="text-[10px] px-2 py-1 border tracking-wider font-bold"
            style={{ color, borderColor: color, background: `${color}10` }}>
            BIAS: {bias.toUpperCase()}
        </span>
    );
}

function LongShortBar({ c }) {
    const longPct = c.long_probability_pct ?? 0;
    const shortPct = c.short_probability_pct ?? 0;
    const waitPct = Math.max(0, 100 - longPct - shortPct);
    const preferred = c.preferred_setup || "WAIT";
    const prefColor = preferred === "LONG" ? "var(--green)" : preferred === "SHORT" ? "var(--red)" : "var(--amber)";
    return (
        <div data-testid="long-short-bar" className="space-y-2">
            <div className="flex items-baseline justify-between text-[11px]">
                <div className="flex items-center gap-2">
                    <span className="font-semibold" style={{ color: "var(--green)" }}>LONG {longPct}%</span>
                    <span className="text-mute">vs</span>
                    <span className="font-semibold" style={{ color: "var(--red)" }}>SHORT {shortPct}%</span>
                </div>
                <span className="text-[10px] font-bold tracking-wider" style={{ color: prefColor }}>
                    PREFERITO: {preferred}
                </span>
            </div>
            <div className="h-2 bg-elev2 flex overflow-hidden">
                <div style={{ width: `${longPct}%`, background: "#22c55e" }} />
                {waitPct > 0 && <div style={{ width: `${waitPct}%`, background: "#eab308" }} />}
                <div style={{ width: `${shortPct}%`, background: "#ef4444" }} />
            </div>
            {c.rationale && (
                <div className="text-[10px] text-mute leading-relaxed">{c.rationale}</div>
            )}
        </div>
    );
}

const PlanRow = ({ label, value, tone, extra }) => {
    const color = tone === "bullish" ? "var(--green)" : tone === "bearish" ? "var(--red)" : "var(--amber)";
    return (
        <div className="flex flex-col">
            <span className="text-[9px] text-mute uppercase tracking-wider">{label}</span>
            <div className="flex items-baseline justify-between gap-1">
                <span className="tnum font-semibold" style={{ color }}>{fmtNum(value)}</span>
                {extra && <span className="text-[9px] text-mute">{extra}</span>}
            </div>
        </div>
    );
};

function Scenarios({ s }) {
    if (!s) return null;
    const bars = [
        { k: "Rialzista", v: s.bullish_pct, color: "#22c55e", reason: s.bullish_rationale },
        { k: "Laterale", v: s.sideways_pct, color: "#eab308", reason: s.sideways_rationale },
        { k: "Ribassista", v: s.bearish_pct, color: "#ef4444", reason: s.bearish_rationale },
    ];
    return (
        <ul className="space-y-2" data-testid="scenarios">
            {bars.map(b => (
                <li key={b.k}>
                    <div className="flex items-center justify-between text-[11px]">
                        <span className="font-semibold" style={{ color: b.color }}>{b.k}</span>
                        <span className="tnum font-bold" style={{ color: b.color }}>{b.v ?? 0}%</span>
                    </div>
                    <div className="h-1 bg-elev2 mt-0.5 relative overflow-hidden">
                        <div className="absolute inset-y-0 left-0" style={{ width: `${b.v ?? 0}%`, background: b.color }} />
                    </div>
                    {b.reason && <div className="text-[10px] text-mute mt-1 leading-relaxed">{b.reason}</div>}
                </li>
            ))}
        </ul>
    );
}

function IndicatorRow({ ind }) {
    const color = TONE_COLORS[ind.signal] || "var(--text-mute)";
    const StrengthIcon = ind.strength === "strong" ? Zap : ind.strength === "moderate" ? Activity : Minus;
    return (
        <li className="text-[11px] flex flex-col gap-0.5 border-l-2 pl-2" style={{ borderColor: color }}>
            <div className="flex items-baseline justify-between gap-2">
                <span className="font-semibold text-dim">{ind.name}</span>
                <span className="tnum text-mute">{typeof ind.value === "number" ? fmtNum(ind.value) : ind.value}</span>
            </div>
            <div className="flex items-center gap-2 text-[10px]">
                <span className="uppercase tracking-wider font-semibold" style={{ color }}>{ind.signal}</span>
                <StrengthIcon size={10} className="text-mute" />
                <span className="text-mute">{ind.strength}</span>
            </div>
            {ind.impact && <div className="text-[10px] text-mute leading-relaxed">{ind.impact}</div>}
        </li>
    );
}

// ---------- Volume analysis ----------

const CONFIRMATION_LABEL = {
    bullish_confirmation: { txt: "Conferma rialzista", color: "var(--green)" },
    bearish_confirmation: { txt: "Conferma ribassista", color: "var(--red)" },
    weak_uptrend_possible_exhaustion: { txt: "Esaurimento rialzo?", color: "var(--amber)" },
    weak_downtrend_possible_exhaustion: { txt: "Esaurimento ribasso?", color: "var(--amber)" },
    weak_signal: { txt: "Segnale debole", color: "var(--text-mute)" },
};

const AD_LABEL = {
    accumulation: { txt: "Accumulazione", color: "var(--green)" },
    distribution: { txt: "Distribuzione", color: "var(--red)" },
    neutral: { txt: "Neutrale", color: "var(--text-mute)" },
};

const VWAP_LABEL = {
    well_above: "Molto sopra VWAP",
    above: "Sopra VWAP",
    below: "Sotto VWAP",
    well_below: "Molto sotto VWAP",
};

const BREAKOUT_LABEL = {
    strong: { txt: "Forte", color: "var(--green)" },
    moderate: { txt: "Moderato", color: "var(--brand-blue)" },
    weak: { txt: "Debole", color: "var(--amber)" },
    false_breakout_risk: { txt: "Rischio falso", color: "var(--red)" },
    none: { txt: "Nessuno", color: "var(--text-mute)" },
};

function VolumeAnalysis({ v, onExplain }) {
    if (!v) return null;
    const score = v.volume_score ?? 50;
    const color = score >= 81 ? "#22c55e" : score >= 61 ? "#3b82f6" : score >= 41 ? "#eab308" : "#ef4444";
    const conf = CONFIRMATION_LABEL[v.confirmation] || { txt: v.confirmation || "—", color: "var(--text-mute)" };
    const adp = AD_LABEL[v.accumulation_distribution] || { txt: "—", color: "var(--text-mute)" };
    const vwap = v.vwap_analysis || {};
    const bv = v.breakout_validation || {};
    const bvLabel = BREAKOUT_LABEL[bv.rating] || BREAKOUT_LABEL.none;
    const hvns = (v.volume_profile && v.volume_profile.hvn_levels) || [];
    const lvns = (v.volume_profile && v.volume_profile.lvn_levels) || [];
    const divs = v.divergences || [];

    return (
        <div data-testid="volume-analysis" className="space-y-3 text-[11px]">
            {/* Volume score */}
            <div>
                <div className="flex items-end gap-2">
                    <div className="text-3xl font-bold tnum" style={{ color }}>{score}</div>
                    <div className="text-[9px] text-mute pb-1.5">/100</div>
                    <div className="ml-auto text-[10px] tracking-wider font-semibold" style={{ color }}>
                        {v.score_label || "—"}
                    </div>
                </div>
                <div className="mt-1 h-1.5 bg-elev2 relative overflow-hidden">
                    <div className="absolute inset-y-0 left-0 transition-all" style={{ width: `${score}%`, background: color }} />
                </div>
            </div>

            {/* Trend / confirmation / A/D / VWAP grid */}
            <div className="grid grid-cols-2 gap-2">
                <VolumeStat label="Trend volumi" value={v.trend} detail={v.trend_detail}
                    onExplain={onExplain} sectionType="trend_volumi"
                    sectionData={{ trend: v.trend, detail: v.trend_detail }} />
                <VolumeStat label="Conferma" value={conf.txt} valueColor={conf.color} detail={v.confirmation_detail}
                    onExplain={onExplain} sectionType="conferma"
                    sectionData={{ confirmation: conf.txt, detail: v.confirmation_detail }} />
                <VolumeStat label="Fase" value={adp.txt} valueColor={adp.color} detail={v.accumulation_distribution_detail}
                    onExplain={onExplain} sectionType="fase"
                    sectionData={{ phase: adp.txt, detail: v.accumulation_distribution_detail }} />
                <VolumeStat label="VWAP"
                    value={VWAP_LABEL[vwap.position] || "—"}
                    detail={`Distanza ${vwap.distance_pct ?? 0}% — ${vwap.detail || ""}`}
                    onExplain={onExplain} sectionType="vwap"
                    sectionData={{ vwap }} />
            </div>

            {/* Breakout validation */}
            {bv.detected && (
                <div className="border-l-2 pl-2" style={{ borderColor: bvLabel.color }}>
                    <div className="text-[9px] tracking-widest text-mute">VALIDAZIONE BREAKOUT</div>
                    <div className="flex items-baseline gap-2 mt-0.5">
                        <span className="text-[11px] font-bold uppercase" style={{ color: bvLabel.color }}>{bvLabel.txt}</span>
                        <span className="text-[10px] text-mute">{bv.detail}</span>
                    </div>
                </div>
            )}

            {/* Volume profile HVN/LVN — wrapped in a clickable stat so the
                user can request an explanation of the accumulation / rejection
                zones as a whole. */}
            {(hvns.length > 0 || lvns.length > 0) && (
                <ClickableWrap onExplain={onExplain} sectionType="hvn_lvn" sectionLabel="HVN / LVN"
                    sectionData={{ hvns, lvns }}>
                    <div className="grid grid-cols-2 gap-3 pt-1 border-t border-soft">
                        <div>
                            <div className="text-[9px] tracking-widest text-mute mb-1">HVN (accettazione)</div>
                            <ul className="space-y-1">
                                {hvns.length === 0 && <li className="text-mute text-[10px]">—</li>}
                                {hvns.slice(0, 4).map((h, i) => (
                                    <li key={i} className="flex items-baseline justify-between gap-2">
                                        <span className="tnum font-semibold" style={{ color: "var(--brand-blue)" }}>{fmtNum(h.price)}</span>
                                        <span className="text-[9px] text-mute truncate" title={h.detail}>{h.role || ""}</span>
                                    </li>
                                ))}
                            </ul>
                        </div>
                        <div>
                            <div className="text-[9px] tracking-widest text-mute mb-1">LVN (rifiuto/scivolo)</div>
                            <ul className="space-y-1">
                                {lvns.length === 0 && <li className="text-mute text-[10px]">—</li>}
                                {lvns.slice(0, 4).map((lv, i) => (
                                    <li key={i} className="flex items-baseline justify-between gap-2">
                                        <span className="tnum font-semibold text-dim">{fmtNum(lv.price)}</span>
                                        <span className="text-[9px] text-mute truncate" title={lv.detail}>{lv.role || ""}</span>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    </div>
                </ClickableWrap>
            )}

            {/* Divergences */}
            {divs.length > 0 && (
                <ClickableWrap onExplain={onExplain} sectionType="divergenze_prezzo_volume"
                    sectionLabel="Divergenze Prezzo/Volume" sectionData={{ divergences: divs }}>
                    <div className="pt-1 border-t border-soft">
                        <div className="text-[9px] tracking-widest text-mute mb-1">DIVERGENZE PREZZO/VOLUME</div>
                        <ul className="space-y-1">
                            {divs.map((d, i) => {
                                const c = d.type === "bullish_divergence" ? "var(--green)"
                                    : d.type === "bearish_divergence" ? "var(--red)" : "var(--amber)";
                                return (
                                    <li key={i} className="text-[10px] border-l-2 pl-2" style={{ borderColor: c }}>
                                        <span className="font-semibold uppercase" style={{ color: c }}>{d.type.replace(/_/g, " ")}</span>
                                        <div className="text-mute leading-relaxed">{d.detail}</div>
                                    </li>
                                );
                            })}
                        </ul>
                    </div>
                </ClickableWrap>
            )}

            {/* Impact on LONG / SHORT — each clickable independently */}
            <div className="grid grid-cols-2 gap-2 pt-1 border-t border-soft">
                <ClickableWrap onExplain={onExplain} sectionType="impatto_long_short"
                    sectionLabel="Impatto Long"
                    sectionData={{ side: "long", impact: v.impact_on_long }}>
                    <div className="border border-soft px-2 py-1.5" style={{ background: "rgba(34,197,94,0.06)" }}>
                        <div className="text-[9px] tracking-widest mb-1" style={{ color: "var(--green)" }}>IMPATTO LONG</div>
                        <div className="text-[10px] text-dim leading-relaxed">{v.impact_on_long || "—"}</div>
                    </div>
                </ClickableWrap>
                <ClickableWrap onExplain={onExplain} sectionType="impatto_long_short"
                    sectionLabel="Impatto Short"
                    sectionData={{ side: "short", impact: v.impact_on_short }}>
                    <div className="border border-soft px-2 py-1.5" style={{ background: "rgba(239,68,68,0.06)" }}>
                        <div className="text-[9px] tracking-widest mb-1" style={{ color: "var(--red)" }}>IMPATTO SHORT</div>
                        <div className="text-[10px] text-dim leading-relaxed">{v.impact_on_short || "—"}</div>
                    </div>
                </ClickableWrap>
            </div>
        </div>
    );
}

/**
 * ClickableWrap — thin generic wrapper that turns any block into a clickable
 * card firing `onExplain({type,label,data})`. Uses stopPropagation so nested
 * clickable descendants keep working; if `onExplain` is missing it renders
 * children unchanged (zero visual side-effect).
 */
const ClickableWrap = ({ children, onExplain, sectionType, sectionLabel, sectionData }) => {
    if (!onExplain || !sectionType) return <>{children}</>;
    const handle = (e) => {
        e.stopPropagation();
        onExplain({ type: sectionType, label: sectionLabel || sectionType, data: sectionData || {} });
    };
    return (
        <div
            onClick={handle}
            className="cursor-pointer transition-colors hover:bg-white/[0.02] rounded-sm"
            data-testid={`clickable-${sectionType}`}
        >
            {children}
        </div>
    );
};

const VolumeStat = ({ label, value, valueColor, detail, onExplain, sectionType, sectionData }) => {
    const clickable = !!(onExplain && sectionType);
    const handle = clickable ? (e) => {
        e.stopPropagation();
        onExplain({ type: sectionType, label, data: sectionData || {} });
    } : undefined;
    return (
        <div
            onClick={handle}
            className={"border border-soft px-2 py-1.5 " + (clickable ? "cursor-pointer hover:bg-white/[0.03] transition-colors" : "")}
            style={{ background: "rgba(255,255,255,0.02)" }}
            data-testid={clickable ? `vol-stat-${sectionType}` : undefined}
        >
            <div className="text-[9px] tracking-widest text-mute">{label.toUpperCase()}</div>
            <div className="text-[11px] font-semibold" style={{ color: valueColor || "var(--text-dim)" }}>{value || "—"}</div>
            {detail && <div className="text-[9px] text-mute mt-0.5 leading-snug">{detail}</div>}
        </div>
    );
};

// ---------- Market Context (Relative Strength + Regime + Smart SL/TP) ----------

const REGIME_COLOR = {
    "Strong Bull Trend": { c: "var(--green)", bg: "rgba(34,197,94,0.10)" },
    "Bull Trend": { c: "#86efac", bg: "rgba(34,197,94,0.06)" },
    "Range Market": { c: "var(--amber)", bg: "rgba(234,179,8,0.06)" },
    "High Volatility": { c: "var(--red)", bg: "rgba(239,68,68,0.06)" },
    "Bear Trend": { c: "#fca5a5", bg: "rgba(239,68,68,0.06)" },
    "Strong Bear Trend": { c: "var(--red)", bg: "rgba(239,68,68,0.10)" },
};

const RR_COLOR = (label) => label === "Eccellente" ? "var(--green)"
    : label === "Buono" ? "#86efac"
    : label === "Accettabile" ? "var(--amber)"
    : "var(--red)";

function MarketContext({ mc }) {
    const rs = mc.relative_strength || {};
    const mr = mc.market_regime || {};
    const long = mc.smart_levels?.long || {};
    const short = mc.smart_levels?.short || {};
    return (
        <div data-testid="market-context" className="space-y-4 text-[11px]">
            {/* Relative Strength */}
            {rs.available ? (
                <div>
                    <div className="text-[9px] tracking-widest text-mute mb-1">RELATIVE STRENGTH</div>
                    <div className="flex items-end gap-2">
                        <div className="text-2xl font-bold tnum" style={{ color: scoreColor(rs.score) }}>{rs.score}</div>
                        <div className="text-[9px] text-mute pb-1">/100</div>
                        <div className="ml-auto text-[10px] tracking-wider font-semibold" style={{ color: scoreColor(rs.score) }}>{rs.label}</div>
                    </div>
                    <div className="text-[10px] text-dim mt-1 leading-relaxed">{rs.narrative}</div>
                    <div className="text-[9px] text-mute mt-1">Benchmark: {rs.benchmark}</div>
                    {rs.components && (
                        <div className="grid grid-cols-4 gap-1 mt-2">
                            {Object.entries(rs.components).map(([k, v]) => (
                                <div key={k} className="text-center border border-soft py-1">
                                    <div className="text-[8px] tracking-wider text-mute uppercase">{k}</div>
                                    {v.out != null ? (
                                        <div className="text-[10px] font-bold tnum" style={{ color: v.out >= 0 ? "var(--green)" : "var(--red)" }}>
                                            {v.out >= 0 ? "+" : ""}{v.out}%
                                        </div>
                                    ) : <div className="text-[10px] text-mute">—</div>}
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            ) : (
                <div className="text-[10px] text-mute">Relative Strength non disponibile (benchmark mancante).</div>
            )}

            {/* Market Regime */}
            <div>
                <div className="text-[9px] tracking-widest text-mute mb-1">MARKET REGIME</div>
                <div className="px-2 py-1.5 border" style={{ borderColor: (REGIME_COLOR[mr.regime] || {}).c, background: (REGIME_COLOR[mr.regime] || {}).bg }}>
                    <div className="text-[11px] font-bold uppercase tracking-wide" style={{ color: (REGIME_COLOR[mr.regime] || {}).c }}>{mr.regime}</div>
                    <div className="text-[10px] text-dim leading-relaxed mt-0.5">{mr.interpretation}</div>
                    {mr.favorable_strategies?.length > 0 && (
                        <div className="flex flex-wrap gap-1 mt-1.5">
                            {mr.favorable_strategies.map((s, i) => (
                                <span key={i} className="text-[8px] tracking-wider uppercase px-1.5 py-0.5 border border-soft text-mute">{s}</span>
                            ))}
                        </div>
                    )}
                </div>
            </div>

            {/* Smart Levels */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                <SmartSetup title="SMART LONG" data={long} side="long" />
                <SmartSetup title="SMART SHORT" data={short} side="short" />
            </div>
        </div>
    );
}

function SmartSetup({ title, data, side }) {
    if (!data?.stop_loss) return null;
    const sl = data.stop_loss;
    const tp = data.take_profit || {};
    const rr = data.risk_reward || {};
    const accent = side === "short" ? "var(--red)" : "var(--green)";
    // Surface R/R T2 prominently in the header so the user can size up the
    // setup quality at a glance without scanning the individual targets.
    // Color-coded: green ≥ 2.0, amber 1.5–2.0, red < 1.5 — same thresholds
    // used by the Opportunity Quality Ranking layer.
    const rrT2Raw = rr.target_2?.rr;
    const rrT2 = rrT2Raw == null ? null : Number(rrT2Raw);
    const rrT2Color = rrT2 == null ? "var(--text-mute)"
        : rrT2 >= 2.0 ? "#22c55e"
        : rrT2 >= 1.5 ? "#f59e0b"
        : "#ef4444";
    return (
        <div className="border border-soft" data-testid={`smart-setup-${side}`}>
            <div className="px-2 py-1 border-b border-soft flex items-center justify-between gap-2">
                <span className="text-[10px] tracking-widest font-bold" style={{ color: accent }}>{title}</span>
                {rrT2 != null && (
                    <span
                        data-testid={`smart-setup-${side}-rr-t2`}
                        title="Risk/Reward del Target 2 — verde ≥ 2.0, ambra 1.5-2.0, rosso < 1.5"
                        className="text-[9px] tnum font-bold tracking-wider px-1.5 py-0.5 border"
                        style={{ color: rrT2Color, borderColor: rrT2Color, background: `${rrT2Color}15` }}>
                        R/R T2: {rrT2.toFixed(2)}
                    </span>
                )}
            </div>
            <div className="p-2 space-y-2">
                {/* Stop Loss */}
                <div>
                    <div className="flex items-baseline justify-between">
                        <span className="text-[9px] tracking-widest text-mute">STOP LOSS</span>
                        <span className="text-[9px] text-mute">Confidence {sl.confidence}/100</span>
                    </div>
                    <div className="flex items-baseline gap-2">
                        <span className="tnum font-bold" style={{ color: "var(--red)" }}>${fmtNum(sl.price)}</span>
                        <span className="text-[10px] text-mute tnum">−{sl.risk_pct}% risk</span>
                    </div>
                    <div className="text-[10px] text-mute leading-relaxed mt-0.5">{sl.rationale}</div>
                </div>
                {/* Targets */}
                {["target_1", "target_2", "target_3"].map((k, i) => {
                    const t = tp[k];
                    const r = rr[k] || {};
                    if (!t) return null;
                    return (
                        <div key={k} className="border-t border-soft pt-1.5">
                            <div className="flex items-baseline justify-between">
                                <span className="text-[9px] tracking-widest text-mute">TARGET {i + 1}</span>
                                <span className="text-[9px] tnum px-1.5 py-0.5 border" style={{ color: RR_COLOR(r.rr_label), borderColor: RR_COLOR(r.rr_label) }}>R/R {r.rr ?? "—"} · {r.rr_label}</span>
                            </div>
                            <div className="flex items-baseline gap-2">
                                <span className="tnum font-bold" style={{ color: accent }}>${fmtNum(t.price)}</span>
                                <span className="text-[10px] text-mute tnum">+{t.potential_pct}% potential</span>
                                <span className="ml-auto text-[9px] text-mute">Conf. {t.confidence}/100</span>
                            </div>
                            <div className="text-[10px] text-mute mt-0.5">{t.source}</div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
}

const SkeletonState = () => (
    <div className="p-4 space-y-3">
        <div className="text-xs text-mute flex items-center gap-2">
            <Bot size={12} className="animate-pulse" />
            Analisi in corso (multi-timeframe + Claude Sonnet 4.5)…
        </div>
        <div className="h-2 bg-elev2 animate-pulse w-3/4" />
        <div className="h-2 bg-elev2 animate-pulse w-1/2" />
        <div className="h-12 bg-elev2 animate-pulse" />
    </div>
);

// --- Liquidity & Trend Structure panel (Modifica 10) -------------------- //

function LiquidityPanel({ liquidity, trendStructure }) {
    const eqhEql = liquidity?.eqh_eql || {};
    const fvg = liquidity?.fvg || {};
    const ob = liquidity?.ob || {};
    const sh = liquidity?.stop_hunt || {};
    const mom = liquidity?.momentum || {};
    const ts = trendStructure || {};

    const momColor = mom.momentum_score == null ? "var(--text-mute)"
        : mom.momentum_score >= 70 ? "var(--green)"
        : mom.momentum_score >= 40 ? "var(--amber)" : "var(--red)";
    const structColor = ts.structure_label === "HH/HL INTATTA" ? "var(--green)"
        : ts.structure_label === "LH/LL INTATTA" ? "var(--red)"
        : "var(--text-mute)";

    return (
        <div data-testid="liquidity-panel" className="space-y-3 text-[11px]">
            {/* Trend Structure header */}
            {ts.structure_label && (
                <div className="border border-soft p-2" data-testid="trend-structure">
                    <div className="flex items-center justify-between gap-2 mb-1">
                        <span className="text-[9px] tracking-widest text-mute">TREND STRUCTURE INTEGRITY</span>
                        {ts.tsi_multiplier != null && (
                            <span className="text-[9px] text-mute tnum">TSI×{ts.tsi_multiplier}</span>
                        )}
                    </div>
                    <div className="font-bold text-xs tracking-wide" style={{ color: structColor }}>
                        {ts.structure_label}
                    </div>
                    {ts.correction_label && (
                        <div className="text-[10px] text-dim mt-0.5">{ts.correction_label}</div>
                    )}
                    {ts.reversal_volume === true && (
                        <div className="text-[10px] mt-1" style={{ color: "var(--amber)" }}>
                            ⚠ Volumi di inversione rilevati (recent vol &gt; 1.2× media)
                        </div>
                    )}
                    {ts.pqs_floor != null && (
                        <div className="text-[9px] text-mute mt-1">PQS floor attivo: {ts.pqs_floor}</div>
                    )}
                </div>
            )}

            {/* Momentum block */}
            {mom.momentum_score != null && (
                <div className="border border-soft p-2" data-testid="liquidity-momentum">
                    <div className="text-[9px] tracking-widest text-mute mb-1">MOMENTUM (ROC5 + THRUST + DIVERGENZA)</div>
                    <div className="flex items-end gap-2">
                        <span className="text-2xl font-bold tnum leading-none" style={{ color: momColor }}>{mom.momentum_score}</span>
                        <span className="text-[10px] text-mute pb-1">/100</span>
                        <span className="ml-auto text-[10px] font-bold tracking-wider" style={{ color: momColor }}>{mom.momentum_label}</span>
                    </div>
                    <div className="text-[10px] text-dim mt-1 grid grid-cols-3 gap-1">
                        {mom.roc5 != null && <div>ROC5: <span className="tnum font-bold">{mom.roc5.toFixed(2)}%</span></div>}
                        {mom.thrust_quality_pct != null && <div>Thrust: <span className="tnum font-bold">{mom.thrust_quality_pct.toFixed(0)}%</span></div>}
                        {mom.divergence_ok != null && <div>Div: <span className="font-bold" style={{ color: mom.divergence_ok ? "var(--green)" : "var(--red)" }}>{mom.divergence_ok ? "OK" : "NO"}</span></div>}
                    </div>
                    {mom.momentum_warning && (
                        <div className="text-[10px] mt-1" style={{ color: "var(--amber)" }}>
                            ⚠ Momentum in rallentamento (ROC5 corrente &lt; ROC5 precedente)
                        </div>
                    )}
                </div>
            )}

            {/* Stop Hunt badge */}
            {sh.stop_hunt_detected && (
                <div className="border border-soft p-2" data-testid="stop-hunt"
                    style={{ borderColor: "#a855f7", background: "rgba(168,85,247,0.08)" }}>
                    <div className="text-[9px] tracking-widest mb-0.5" style={{ color: "#a855f7" }}>STOP HUNT</div>
                    <div className="font-bold text-xs">⚡ {sh.badge}</div>
                    {sh.latest_sweep && (
                        <div className="text-[10px] text-dim mt-0.5">
                            {sh.latest_sweep.level_type} sweep a ${sh.latest_sweep.level_swept?.toFixed?.(2)} · direzione {sh.latest_sweep.direction_after} · {sh.latest_sweep.candles_ago} candle fa
                        </div>
                    )}
                </div>
            )}

            {/* EQH / EQL levels */}
            {(eqhEql.nearest_eqh != null || eqhEql.nearest_eql != null) && (
                <div className="border border-soft p-2" data-testid="liquidity-eqh-eql">
                    <div className="text-[9px] tracking-widest text-mute mb-1">EQUAL HIGH / EQUAL LOW</div>
                    <div className="grid grid-cols-2 gap-2 text-[11px] tnum">
                        <div>
                            <div className="text-[9px] text-mute">Più vicino sopra (EQH)</div>
                            <div className="font-bold" style={{ color: "var(--red)" }}>
                                {eqhEql.nearest_eqh != null ? `$${Number(eqhEql.nearest_eqh).toFixed(2)}` : "—"}
                            </div>
                            {eqhEql.sl_short != null && (
                                <div className="text-[9px] text-mute">SL short: ${Number(eqhEql.sl_short).toFixed(2)}</div>
                            )}
                        </div>
                        <div>
                            <div className="text-[9px] text-mute">Più vicino sotto (EQL)</div>
                            <div className="font-bold" style={{ color: "var(--green)" }}>
                                {eqhEql.nearest_eql != null ? `$${Number(eqhEql.nearest_eql).toFixed(2)}` : "—"}
                            </div>
                            {eqhEql.sl_long != null && (
                                <div className="text-[9px] text-mute">SL long: ${Number(eqhEql.sl_long).toFixed(2)}</div>
                            )}
                        </div>
                    </div>
                    <div className="text-[9px] text-mute mt-1">
                        Cluster rilevati: {eqhEql.eqh_levels?.length || 0} EQH · {eqhEql.eql_levels?.length || 0} EQL
                    </div>
                </div>
            )}

            {/* Fair Value Gaps */}
            {fvg.fvg_list?.length > 0 && (
                <div className="border border-soft p-2" data-testid="liquidity-fvg">
                    <div className="text-[9px] tracking-widest text-mute mb-1">FAIR VALUE GAPS</div>
                    <div className="grid grid-cols-2 gap-2 text-[11px] tnum">
                        {fvg.nearest_bullish_fvg && (
                            <div>
                                <div className="text-[9px]" style={{ color: "var(--green)" }}>FVG bullish (entry long)</div>
                                <div className="font-bold">${Number(fvg.nearest_bullish_fvg.mid).toFixed(2)}</div>
                                <div className="text-[9px] text-mute">{Number(fvg.nearest_bullish_fvg.bottom).toFixed(2)} → {Number(fvg.nearest_bullish_fvg.top).toFixed(2)}</div>
                            </div>
                        )}
                        {fvg.nearest_bearish_fvg && (
                            <div>
                                <div className="text-[9px]" style={{ color: "var(--red)" }}>FVG bearish (entry short)</div>
                                <div className="font-bold">${Number(fvg.nearest_bearish_fvg.mid).toFixed(2)}</div>
                                <div className="text-[9px] text-mute">{Number(fvg.nearest_bearish_fvg.bottom).toFixed(2)} → {Number(fvg.nearest_bearish_fvg.top).toFixed(2)}</div>
                            </div>
                        )}
                    </div>
                    <div className="text-[9px] text-mute mt-1">{fvg.fvg_list.length} FVG totali rilevate</div>
                </div>
            )}

            {/* Order Blocks */}
            {(ob.nearest_bullish_ob || ob.nearest_bearish_ob) && (
                <div className="border border-soft p-2" data-testid="liquidity-ob">
                    <div className="text-[9px] tracking-widest text-mute mb-1">ORDER BLOCKS</div>
                    <div className="grid grid-cols-2 gap-2 text-[11px] tnum">
                        {ob.nearest_bullish_ob && (
                            <div>
                                <div className="text-[9px]" style={{ color: "var(--green)" }}>OB bullish</div>
                                <div className="font-bold">${Number(ob.nearest_bullish_ob.mid).toFixed(2)}</div>
                                <div className="text-[9px] text-mute">{Number(ob.nearest_bullish_ob.bottom).toFixed(2)} → {Number(ob.nearest_bullish_ob.top).toFixed(2)} · impulso {Number(ob.nearest_bullish_ob.impulse_size_pct).toFixed(1)}%</div>
                            </div>
                        )}
                        {ob.nearest_bearish_ob && (
                            <div>
                                <div className="text-[9px]" style={{ color: "var(--red)" }}>OB bearish</div>
                                <div className="font-bold">${Number(ob.nearest_bearish_ob.mid).toFixed(2)}</div>
                                <div className="text-[9px] text-mute">{Number(ob.nearest_bearish_ob.bottom).toFixed(2)} → {Number(ob.nearest_bearish_ob.top).toFixed(2)} · impulso {Number(ob.nearest_bearish_ob.impulse_size_pct).toFixed(1)}%</div>
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}
